import { Shield, Check, Phone, Star, MapPin } from "lucide-react";
import PestOnlyCalculator from "../components/PestOnlyCalculator";
import { Logo, Header } from "../components/Layout";

const suburbs = ["Redbank Plains", "Redbank", "Collingwood Park", "Bellbird Park", "Goodna", "Riverview", "Dinmore", "Bundamba"];

export default function PestControlRedbankPlainsPage() {
  return (
    <>
      <>
        <title>Pest Control Redbank Plains | Pest Free Ipswich & Brisbane</title>
        <meta name="description" content="Professional pest control for Redbank Plains, Collingwood Park, Bellbird Park & surrounds. Cockroaches, ants, termites & bird proofing. Instant quote." />
      </>

      <Header />
      <div className="min-h-screen bg-slate-50">
        {/* Hero */}
        <section className="bg-gradient-to-br from-[#8B2500] via-[#6B1D00] to-[#E74C3C]/20 text-white pt-28 pb-16 md:pt-32 md:pb-24">
          <div className="max-w-6xl mx-auto px-4">
            <div className="grid md:grid-cols-2 gap-10 items-center">
              <div>
                <div className="inline-flex items-center gap-2 bg-[#E74C3C]/20 border border-[#E74C3C]/30 rounded-full px-4 py-1.5 mb-5">
                  <MapPin className="w-4 h-4 text-[#E74C3C]" />
                  <span className="text-sm font-medium text-[#E74C3C]">Redbank Plains & Surrounds</span>
                </div>
                <h1 className="text-4xl md:text-5xl font-black mb-4 leading-tight">
                  Pest Control <span className="text-[#7ABC32]">Redbank Plains</span>
                </h1>
                <p className="text-lg text-slate-300 mb-6">
                  Fast-growing Redbank Plains needs reliable pest control. From cockroaches and ants to termites and bird proofing — we've got your home covered.
                </p>
                <div className="flex flex-wrap gap-3 mb-6">
                  <div className="flex items-center gap-1.5 text-sm text-slate-300"><Check className="w-4 h-4 text-[#7ABC32]" />Same Day Service</div>
                  <div className="flex items-center gap-1.5 text-sm text-slate-300"><Check className="w-4 h-4 text-[#7ABC32]" />QBCC Licensed</div>
                  <div className="flex items-center gap-1.5 text-sm text-slate-300"><Check className="w-4 h-4 text-[#7ABC32]" />Family Safe</div>
                </div>
                <div className="flex items-center gap-3">
                  <a href="tel:0737081422" className="flex items-center gap-2 bg-[#7ABC32] hover:bg-[#6AAD28] text-white font-semibold px-6 py-3 rounded-full transition-all">
                    <Phone className="w-5 h-5" /> (07) 3708 1422
                  </a>
                  <div className="flex items-center gap-1">
                    {[...Array(5)].map((_, i) => <Star key={i} className="w-4 h-4 text-yellow-400 fill-yellow-400" />)}
                    <span className="text-sm text-slate-300 ml-1">4.9 on Google</span>
                  </div>
                </div>
              </div>
              <div className="flex justify-center">
                <Logo className="h-32 md:h-40 opacity-90" />
              </div>
            </div>
          </div>
        </section>

        {/* Calculator */}
        <section className="py-12 md:py-16" id="calculator">
          <div className="max-w-4xl mx-auto px-4">
            <div className="text-center mb-8">
              <h2 className="text-2xl md:text-3xl font-bold text-slate-800">Get Your Instant Price</h2>
              <p className="text-slate-600 mt-2">Pest control & termite inspection — instant quote</p>
            </div>
            <PestOnlyCalculator source="redbank_plains" />
          </div>
        </section>

        {/* Common Issues */}
        <section className="py-12 bg-white">
          <div className="max-w-5xl mx-auto px-4">
            <h2 className="text-2xl font-bold text-slate-800 text-center mb-8">Common Pest Issues in Redbank Plains</h2>
            <div className="grid md:grid-cols-2 gap-6">
              {[
                { title: "Cockroaches & Ants", desc: "New estates and proximity to older Ipswich suburbs mean constant pest pressure from cockroaches, ants and silverfish.", color: "#E74C3C" },
                { title: "Spiders", desc: "Bushland borders throughout Redbank Plains bring huntsmen, redbacks and other spiders close to homes.", color: "#E74C3C" },
                { title: "Termites", desc: "The Ipswich region is one of the highest termite-risk areas in Queensland. Regular inspections are essential for Redbank Plains homes.", color: "#D2691E" },
                { title: "Pigeons & Birds", desc: "Solar panels on new builds attract nesting pigeons — bird proofing protects your panels and prevents costly damage.", color: "#5BC0DE" },
              ].map((item, i) => (
                <div key={i} className="flex gap-4 p-4 rounded-lg bg-slate-50">
                  <div className="w-10 h-10 rounded-full flex items-center justify-center flex-shrink-0" style={{ backgroundColor: item.color + '15' }}>
                    <Shield className="w-5 h-5" style={{ color: item.color }} />
                  </div>
                  <div>
                    <h3 className="font-bold text-slate-800">{item.title}</h3>
                    <p className="text-sm text-slate-600 mt-1">{item.desc}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Suburbs */}
        <section className="py-12 bg-slate-50">
          <div className="max-w-5xl mx-auto px-4 text-center">
            <h2 className="text-2xl font-bold text-slate-800 mb-6">Serving Redbank Plains & Surrounds</h2>
            <div className="flex flex-wrap justify-center gap-2">
              {suburbs.map(s => (
                <span key={s} className="bg-white border border-slate-200 rounded-full px-4 py-2 text-sm font-medium text-slate-700">{s}</span>
              ))}
            </div>
            <p className="text-slate-500 text-sm mt-4">And all surrounding areas</p>
          </div>
        </section>

        {/* CTA */}
        <section className="py-12 bg-gradient-to-r from-[#E74C3C] to-[#c0392b] text-white text-center">
          <div className="max-w-3xl mx-auto px-4">
            <h2 className="text-2xl md:text-3xl font-bold mb-4">Redbank Plains' Local Pest Experts</h2>
            <p className="text-lg text-white/80 mb-6">Get your instant quote above or call us now for same-day service.</p>
            <a href="tel:0737081422" className="inline-flex items-center gap-2 bg-white text-[#E74C3C] font-bold px-8 py-3 rounded-full hover:bg-slate-100 transition-all">
              <Phone className="w-5 h-5" /> Call (07) 3708 1422
            </a>
          </div>
        </section>
      </div>
    </>
  );
}
